import { Box } from '@mui/material';
import { useMemo, useState } from 'react';
import { SystemModelMap, type SystemAssigned } from '@consts/systems';
import { useStyles } from './styles/UnassignedTable.styles';
import { AppRoutes } from '@consts/routes';
import { Link, useNavigate } from 'react-router-dom';
import { type ColumnOptions, DataTable } from '@components/tables/DataTable';
import { useNameMapQuery } from '@features/redux/apis/org';
import { useSystemsQuery, useSystemsBySerialNumQuery } from '@features/redux/apis/system';
import { SystemTableEffects } from './SystemTableEffects';
import { LastConnected } from '@components/LastConnected';
import { useSelector } from 'react-redux';

import type { SystemFilter } from '@/types/systems';
import type { System } from '@api/system';
import type { Location } from '@/types/locations';
import type { RootState } from '@features/redux/store';
import { ExportSystems } from '@components/buttons/ExportSystems';
import { getFilterParams } from '@utils/filters';
import { type BillingType, CUT_FEE_MAP, type WarrantyType, WARRANTY_PLAN_MAP } from '@consts/company';

const PRODUCT_ID_MAP: Record<string, string> = {
    4400: 'Graphtec CE6000(+)',
    4405: 'Graphtec CE7000',
    10: 'ADSI',
    67: 'RCMini',
};

const PAGE_SIZE = 20;

interface SystemTableProps {
    formattedLocations?: Location[];
    assigned: SystemAssigned;
    searchTerm: string;
    filters: SystemFilter;
    orgFilters: {
        companyIds: string;
        resellerIds: string;
        locationIds: string;
    };
}

export const SystemTable = ({ formattedLocations, assigned, searchTerm, filters, orgFilters }: SystemTableProps) => {
    const classes = useStyles();
    const navigate = useNavigate();

    const role = useSelector((state: RootState) => state.auth.Roles.role);

    const { data: nameMap, isLoading: nameMapLoading } = useNameMapQuery({});
    const [pageNum, setPageNum] = useState(1);

    const searchQuery = useSystemsBySerialNumQuery(
        {
            type: assigned,
            serialNum: searchTerm,
        },
        { skip: nameMapLoading || !searchTerm }
    );
    const { data: searchData, isFetching: searchFetching } = searchQuery;

    const systemsQuery = useSystemsQuery(
        {
            ...orgFilters,
            ...getFilterParams(filters),
            type: assigned,
            pageNum: pageNum - 1,
            pageSize: PAGE_SIZE,
        },
        { skip: nameMapLoading || Boolean(searchTerm) }
    );
    const { data: systemsData, isFetching: systemsFetching } = systemsQuery;

    const systems = searchTerm ? searchData : systemsData?.data;
    const loading = nameMapLoading || (searchTerm ? searchFetching : systemsFetching);
    const totalRecords = searchTerm ? searchData?.length : systemsData?.total;

    const columns = useMemo(() => {
        const baseColumns = [
            {
                field: 'LocationID',
                headerName: 'Location',
                valueGetter: ({ value }) => {
                    return nameMap?.locations[value];
                },
                renderCell: ({ row }) => {
                    return row.LocationID ? (
                        <Link
                            style={{
                                paddingRight: '20px',
                                paddingTop: '20px',
                                paddingBottom: '20px',
                            }}
                            to={`${AppRoutes.Organizations}?type=location&id=${row.LocationID}`}
                            onClick={(e) => e.stopPropagation()}>
                            {nameMap?.locations[row.LocationID as number]}
                        </Link>
                    ) : (
                        '-'
                    );
                },
            },

            {
                field: 'ResellerID',
                headerName: 'Reseller',
                valueGetter: ({ value }) => {
                    return nameMap?.resellers[value];
                },
                renderCell: ({ row }) => {
                    return row.ResellerID ? (
                        <Link
                            style={{
                                paddingRight: '20px',
                                paddingTop: '20px',
                                paddingBottom: '20px',
                            }}
                            to={`${AppRoutes.Organizations}?type=reseller&id=${row.ResellerID}`}
                            onClick={(e) => e.stopPropagation()}>
                            {nameMap?.resellers[row.ResellerID as number]}
                        </Link>
                    ) : (
                        '-'
                    );
                },
            },
            {
                field: 'CompanyID',
                headerName: 'Company',
                valueGetter: ({ value }) => {
                    return nameMap?.companies[value];
                },
                renderCell: ({ row }) => {
                    return row.CompanyID ? (
                        <Link
                            style={{
                                paddingRight: '20px',
                                paddingTop: '20px',
                                paddingBottom: '20px',
                            }}
                            to={`${AppRoutes.Organizations}?type=company&id=${row.CompanyID}`}
                            onClick={(e) => e.stopPropagation()}>
                            {nameMap?.companies[row.CompanyID as number]}
                        </Link>
                    ) : (
                        '-'
                    );
                },
            },

            {
                field: 'SerialNum',
                headerName: 'Serial Number',
            },
            {
                field: 'Model',
                headerName: 'Machine Model',
                valueGetter: ({ value }) => {
                    return SystemModelMap[value];
                },
            },
            {
                field: 'CreatedDate',
                headerName: 'Created Date',
                formatValue: (val) => {
                    return val ? new Intl.DateTimeFormat().format(new Date(val)) : '';
                },
            },
            {
                field: 'Status',
                headerName: 'Status',
            },
            {
                field: 'LastConnected',
                headerName: 'Last Connected',
                formatValue: (val) => <LastConnected timestamp={val} />,
                width: 130,
            },
            {
                field: 'CutPlanID',
                headerName: 'Billing Plan',
                formatValue: (val) => {
                    return val !== undefined && val !== null ? CUT_FEE_MAP[val as BillingType] : '';
                },
            },
            {
                field: 'WarrantyPlanID',
                headerName: 'Warranty Plan',
                formatValue: (val) => {
                    return val !== undefined && val !== null ? WARRANTY_PLAN_MAP[val as WarrantyType] : '';
                },
            },
            // {
            //     field: 'ProductID',
            //     headerName: 'Cutter Vendor',
            //     formatValue: (val) => PRODUCT_ID_MAP[val] || 'Unknown',
            // },
        ] as ColumnOptions<System>[];

        if (formattedLocations) {
            // Insert location column after reseller, if locations are provided
            baseColumns.splice(2, 0, {
                field: 'LocationID',
                headerName: 'Location',
                valueGetter: ({ value }) => {
                    return nameMap?.locations[value];
                },
            });
        }

        return baseColumns;
    }, [nameMap]);

    return (
        <Box display={'flex'} flexDirection={'column'} width={'100%'} flex={1} sx={{ minWidth: 0 }}>
            <SystemTableEffects queries={[searchQuery, systemsQuery]} />

            <DataTable
                storageKey='Systems'
                columnOrdering={true}
                page={pageNum}
                displayTotal={false}
                height={'calc(100dvh - 250px)'}
                virtualize={false}
                rows={systems ?? []}
                columns={columns}
                getUniqueRowId={(row) => row.StationID?.toString() ?? ''}
                pageSize={PAGE_SIZE}
                totalRowCount={totalRecords || 0}
                pagination={!searchTerm}
                onRowClick={
                    role.stores.edit
                        ? ({ row }) => navigate(`${AppRoutes.Organizations}?type=system&id=${row.StationID}`)
                        : undefined
                }
                onPaginate={setPageNum}
                loading={loading}
            />

            <Box display={'flex'} flexDirection={'row'} justifyContent={'flex-end'} pt={'10px'} pr={'20px'}>
                <ExportSystems type={assigned} params={orgFilters} />
            </Box>
        </Box>
    );
};
